import React, { useEffect, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import { PatientHistoryType } from 'src/graphql.generated'
import {
  CALL_STATUS_OPTIONS,
  CHECK_IN_CALL_NOTE_SCHEMA,
  CallStatus,
  shouldShowFullCheckinCallForm,
} from 'src/participantHistoryForm/form/checkinCall/CheckinCallNoteSchema'
import { useLinkedCallPrefill } from '../../../../dashboard/callNotesRequest'
import { useDraftSaver, useRestoredDraft } from '../../../../api/historyDraft'
import { usePatientHistoryPrefill } from '../../../../api/usePatientHistoryPrefill'
import { useSubmitPatientHistory } from '../../../../api/useSubmitPatientHistory'
import { useUncancelledInProgramCalls } from '../../../../api/useUncancelledInProgramCalls'
import { formatUsDateTime } from '../../../../format'
import { BillingCode } from '../billingCodes'
import BillingCodesSelect from '../BillingCodesSelect'
import {
  CheckboxGroup,
  CheckboxGroupWithOther,
  DraftNotice,
  FieldGrid,
  FieldSection,
  FormActions,
  LabeledDate,
  LabeledRadioGroup,
  LabeledSelect,
  LabeledText,
  LabeledTime,
  MissingFields,
  todayIso,
  useCheckboxGroup,
  useCheckboxGroupWithOther,
  useRequiredFields,
} from '../../../fieldPrimitives'
import { layoutForType } from '../../historyLayouts'
import PrefillLoading from '../PrefillLoading'
import { ValidationIssues, validationIssues } from '../validation'
import { codesAt, groupAt, textAt } from '../valuePath'
import {
  SERVICE_DELIVERY_OPTIONS,
  REASON_FOR_CALL_OPTIONS,
  INTERVENTIONS_OPTIONS,
  PROTECTIVE_FACTORS_OPTIONS,
  NEXT_STEPS_OPTIONS,
  PARTICIPANT_RESPONSE_OPTIONS,
  PLAN_OPTIONS,
} from '../../historyOptions'

const allOff = (options: { key: string }[]) => Object.fromEntries(options.map((option) => [option.key, false]))

const SERVICE_DELIVERY_DEFAULTS = Object.fromEntries(SERVICE_DELIVERY_OPTIONS.map((option) => [option.key, true]))

const timeOnDate = (date: string, time: string) =>
  time ? DateTime.fromISO(`${date}T${time}`).toUTC().toISO() ?? '' : ''

const timeOfDay = (iso: string) => (iso ? DateTime.fromISO(iso).toFormat('HH:mm') : '')

const CheckInCallForm = ({ uid, prefill }: { uid: string; prefill: Record<string, unknown> }) => {
  const restored = useRestoredDraft(uid, PatientHistoryType.CheckInCall)
  const source = restored ?? prefill
  const [date, setDate] = useState(textAt(restored, 'date') || todayIso())
  const linkedCall = useLinkedCallPrefill()
  const [callID, setCallID] = useState(linkedCall)
  const [firstName, setFirstName] = useState(textAt(source, 'FIRST_NAME'))
  const [lastName, setLastName] = useState(textAt(source, 'LAST_NAME'))
  const [memberID, setMemberID] = useState(textAt(source, 'MEMBER_ID'))
  const [providerName, setProviderName] = useState(textAt(source, 'PROVIDER_NAME'))
  const [licenseType, setLicenseType] = useState(textAt(source, 'LICENSE_TYPE'))
  const [callStatus, setCallStatus] = useState((textAt(restored, 'CALL_STATUS') || 'CALL_HAD') as CallStatus)
  const [description, setDescription] = useState(textAt(restored, 'DESCRIPTION'))
  const [callStartTime, setCallStartTime] = useState(timeOfDay(textAt(restored, 'CALL_START_TIME')))
  const [callEndTime, setCallEndTime] = useState(timeOfDay(textAt(restored, 'CALL_END_TIME')))
  const [serviceDelivery, toggleServiceDelivery] = useCheckboxGroup(
    SERVICE_DELIVERY_OPTIONS,
    groupAt(restored, 'SERVICE_DELIVERY_AND_COMPLIANCE')?.values ?? SERVICE_DELIVERY_DEFAULTS
  )
  const [callMethod, setCallMethod] = useState<'IN_APP' | 'PHONE_CALL'>(
    textAt(restored, 'CALL_METHOD') === 'PHONE_CALL' ? 'PHONE_CALL' : 'IN_APP'
  )
  const [smartGoals, setSmartGoals] = useState(textAt(source, 'SMART_GOALS'))
  const [billingCodes, setBillingCodes] = useState(codesAt(source, 'BILLING_CODES') as BillingCode[])
  const [reasonForCall, setReasonForCall] = useState(
    textAt(restored, 'REASON_FOR_CALL') as (typeof REASON_FOR_CALL_OPTIONS)[number]['value'] | ''
  )
  const interventions = useCheckboxGroupWithOther(INTERVENTIONS_OPTIONS, groupAt(restored, 'INTERVENTIONS'))
  const [riskChanged, setRiskChanged] = useState<'yes' | 'no'>(
    textAt(restored, 'RISK_LEVEL_HAS_CHANGE') === 'yes' ? 'yes' : 'no'
  )
  const protectiveFactors = useCheckboxGroupWithOther(
    PROTECTIVE_FACTORS_OPTIONS,
    groupAt(restored, 'PROTECTIVE_FACTORS_IDENTIFIED')
  )
  const nextSteps = useCheckboxGroupWithOther(NEXT_STEPS_OPTIONS, groupAt(restored, 'NEXT_STEPS'))
  const participantResponse = useCheckboxGroupWithOther(
    PARTICIPANT_RESPONSE_OPTIONS,
    groupAt(restored, 'PARTICIPANT_RESPONSE')
  )
  const plan = useCheckboxGroupWithOther(PLAN_OPTIONS, groupAt(restored, 'PLAN'))
  const [issues, setIssues] = useState<string[]>([])
  const { calls } = useUncancelledInProgramCalls(uid)
  const { submit, submitting, error } = useSubmitPatientHistory(uid)
  const timesPrefilled = useRef(false)

  useEffect(() => {
    const call = calls.find((candidate) => candidate.id === linkedCall)
    const start = call && DateTime.fromISO(call.startTime)

    if (!start?.isValid || restored || timesPrefilled.current) {
      return
    }

    timesPrefilled.current = true
    setDate(start.toISODate() ?? todayIso())
    setCallStartTime(start.toFormat('HH:mm'))
  }, [calls, linkedCall, restored])

  const pickCall = (id: string) => {
    const call = calls.find((candidate) => candidate.id === id)

    setCallID(id)

    if (call) {
      setDate(DateTime.fromISO(call.startTime).toISODate() ?? date)
      setCallStartTime(DateTime.fromISO(call.startTime).toFormat('HH:mm'))
      setCallEndTime('')
    }
  }

  const callHad = shouldShowFullCheckinCallForm(callStatus)

  const required = useRequiredFields({
    'Date of event': Boolean(date),
    'Call start time': Boolean(callStartTime),
    ...(callHad
      ? { 'Call end time': Boolean(callEndTime), 'Billing codes': billingCodes.length > 0 }
      : { Description: Boolean(description.trim()) }),
  })

  const meta = {
    date,
    FIRST_NAME: firstName,
    LAST_NAME: lastName,
    MEMBER_ID: memberID,
    PROVIDER_NAME: providerName,
    LICENSE_TYPE: licenseType,
    CALL_START_TIME: timeOnDate(date, callStartTime),
    CALL_END_TIME: timeOnDate(date, callEndTime),
    SERVICE_DELIVERY_AND_COMPLIANCE: serviceDelivery,
    CALL_STATUS: callStatus,
  }
  const value = callHad
    ? {
        ...meta,
        CALL_METHOD: callMethod,
        SMART_GOALS: smartGoals,
        BILLING_CODES: billingCodes,
        REASON_FOR_CALL: reasonForCall,
        INTERVENTIONS: interventions.merged,
        RISK_LEVEL_HAS_CHANGE: riskChanged,
        PROTECTIVE_FACTORS_IDENTIFIED:
          riskChanged === 'yes' ? protectiveFactors.merged : allOff(PROTECTIVE_FACTORS_OPTIONS),
        NEXT_STEPS: riskChanged === 'yes' ? nextSteps.merged : allOff(NEXT_STEPS_OPTIONS),
        PARTICIPANT_RESPONSE: participantResponse.merged,
        PLAN: plan.merged,
      }
    : { ...meta, DESCRIPTION: description }
  const dirty = useDraftSaver(uid, PatientHistoryType.CheckInCall, value)

  const onSubmit = async () => {
    if (!required.attempt()) {
      return
    }

    const found = validationIssues(CHECK_IN_CALL_NOTE_SCHEMA, value, layoutForType(PatientHistoryType.CheckInCall))

    setIssues(found)

    if (found.length === 0) {
      await submit({
        date,
        type: PatientHistoryType.CheckInCall,
        value,
        ...(callID && { references: { callID } }),
      })
    }
  }

  return (
    <FieldSection>
      <LabeledSelect
        label="Linked check-in call"
        value={callID}
        onChange={pickCall}
        options={[
          { value: '', label: 'None' },
          ...calls.map((call) => ({
            value: call.id,
            label: `${formatUsDateTime(call.startTime)}${call.noteLinked ? ' (note linked)' : ''}`,
          })),
        ]}
      />
      <FieldSection title="Overall information">
        <FieldGrid>
          <LabeledDate
            label="Date of event"
            value={date}
            onChange={setDate}
            required
            error={required.error('Date of event')}
          />
          <LabeledText label="First name" value={firstName} onChange={setFirstName} />
          <LabeledText label="Last name" value={lastName} onChange={setLastName} />
          <LabeledText label="Insurance member ID" value={memberID} onChange={setMemberID} />
          <LabeledText label="Provider name" value={providerName} onChange={setProviderName} />
          <LabeledText label="License type" value={licenseType} onChange={setLicenseType} />
          <LabeledTime
            label="Call start time"
            value={callStartTime}
            onChange={setCallStartTime}
            required
            error={required.error('Call start time')}
          />
          <LabeledTime
            label="Call end time"
            value={callEndTime}
            onChange={setCallEndTime}
            required={callHad}
            error={required.error('Call end time') ? 'Call end time is required' : undefined}
          />
          <LabeledSelect
            label="Call status"
            value={callStatus}
            onChange={setCallStatus}
            options={CALL_STATUS_OPTIONS}
          />
        </FieldGrid>
      </FieldSection>

      <FieldSection title="Service Delivery & Compliance">
        <CheckboxGroup options={SERVICE_DELIVERY_OPTIONS} values={serviceDelivery} onChange={toggleServiceDelivery} />
      </FieldSection>
      {callHad ? (
        <>
          <FieldSection title="Call Method">
            <LabeledRadioGroup
              label=""
              value={callMethod}
              onChange={setCallMethod}
              options={[
                { value: 'IN_APP', label: 'In-app' },
                { value: 'PHONE_CALL', label: 'Phone call' },
              ]}
            />
          </FieldSection>

          <FieldSection title="SMART Goals">
            <LabeledText label="SMART Goals" value={smartGoals} onChange={setSmartGoals} multiline />
          </FieldSection>

          <FieldSection title="Billing and Diagnosis Codes">
            <BillingCodesSelect
              value={billingCodes}
              onChange={setBillingCodes}
              required
              error={required.error('Billing codes')}
            />
          </FieldSection>

          <FieldSection title="Reason for Call">
            <LabeledRadioGroup
              label="Reason for call"
              value={reasonForCall}
              onChange={setReasonForCall}
              options={[...REASON_FOR_CALL_OPTIONS]}
            />
          </FieldSection>

          <FieldSection title="Interventions">
            <CheckboxGroupWithOther
              options={INTERVENTIONS_OPTIONS}
              state={interventions}
              otherLabel="Brief summary of call"
            />
          </FieldSection>

          <FieldSection title="Risk Assessment">
            <LabeledRadioGroup
              label=""
              value={riskChanged}
              onChange={setRiskChanged}
              options={[
                { value: 'no', label: 'No known changes to risk level since last encounter' },
                { value: 'yes', label: 'Changes to risk level' },
              ]}
            />
            {riskChanged === 'yes' && (
              <>
                <FieldSection title="Protective Factors Identified">
                  <CheckboxGroupWithOther
                    options={PROTECTIVE_FACTORS_OPTIONS}
                    state={protectiveFactors}
                    otherLabel="Other (if applicable)"
                  />
                </FieldSection>
                <FieldSection title="Next Steps">
                  <CheckboxGroupWithOther
                    options={NEXT_STEPS_OPTIONS}
                    state={nextSteps}
                    otherLabel="Other (if applicable)"
                  />
                </FieldSection>
              </>
            )}
          </FieldSection>

          <FieldSection title="Participant Response">
            <CheckboxGroupWithOther
              options={PARTICIPANT_RESPONSE_OPTIONS}
              state={participantResponse}
              otherLabel="Other (if applicable)"
            />
          </FieldSection>

          <FieldSection title="Plan">
            <CheckboxGroupWithOther options={PLAN_OPTIONS} state={plan} otherLabel="Other (if applicable)" />
          </FieldSection>
        </>
      ) : (
        <FieldSection title="Description">
          <LabeledText
            label="Description"
            value={description}
            onChange={setDescription}
            multiline
            required
            error={required.error('Description')}
          />
        </FieldSection>
      )}

      <MissingFields labels={required.missing} />
      <ValidationIssues issues={issues} />
      <DraftNotice restored={restored !== null} dirty={dirty} />
      <FormActions onSubmit={onSubmit} disabled={false} submitting={submitting} error={error} />
    </FieldSection>
  )
}

const CheckInCallFields = ({ uid }: { uid: string }) => {
  const { loading, value } = usePatientHistoryPrefill(uid, PatientHistoryType.CheckInCall)

  return loading ? <PrefillLoading /> : <CheckInCallForm uid={uid} prefill={value} />
}

export default CheckInCallFields
